import './react-global' // Must be first: sets React/ApexCharts globals + native download intercept
import './i18n' // i18next + initReactI18next so useTranslation has an instance
import '_style.css' // Global Styles
import 'marketing-site/styles/index.css' // Commercial module global styles at app root

import React, {useEffect, useLayoutEffect, useState} from 'react'
import { createRoot } from 'react-dom/client'
import { HelmetProvider, Helmet } from 'react-v19-helmet-async'
import { BrowserRouter } from 'react-router-dom'
import { SettingsProvider } from 'utils/context/settings'
import { CollapseDrawerProvider } from 'utils/context/drawer'
import App from './App'
import * as serviceWorkerRegistration from './serviceWorkerRegistration'
import reportWebVitals from './reportWebVitals'
import storage from 'utils/storage'
import 'utils/processFlowDispatch' // Centralized process-flow fetch + dispatch (registers listener)
import 'utils/groupStoreDispatch' // Centralized group/store fetch + dispatch (registers listener)
import {
  BOOT_APP_3G_SLOW_EVENT,
  getNavigatorConnection,
  isSlowBootConnectionHint,
} from 'utils/sparkleBootConnection'

const _USERSNAP_GLOBAL_API_KEY = '70ccdab5-9822-416e-b752-ef1d60667f77'
const LOADING_TIMEOUT = 30000

const AppWithLoader = () => {
  const [formattedUser, setFormattedUser] = useState(null)
  const [_offline, _setOffline] = useState(false)
  const [isIOS, setIsIOS] = useState(/iPhone|iPad|iPod/i.test(navigator.userAgent))
  const [loadingState, setLoadingState] = useState(() => {
    if (typeof window !== 'undefined') {
      window.__sparkleAppInitLoading = true
    }
    return {
      isLoading: true,
      error: null,
      retryCount: 0,
    }
  })

  // Sync boot flag + root loader + slow-network signal before paint so SessionAuth can see `__sparkleAppInitLoading`.
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return undefined

    window.__sparkleAppInitLoading = Boolean(loadingState.isLoading && !loadingState.error)

    if (loadingState.error) {
      // Show error in loader
      const loaderText = document.getElementById('root-loader-text')
      if (loaderText) {
        loaderText.innerHTML = `Error: ${loadingState.error}<br/><button id="retry-button" style="margin-top:10px;padding:5px 15px;border-radius:4px;background:#1976d2;color:white;border:none;cursor:pointer;">Retry</button>`
        document.getElementById('retry-button')?.addEventListener('click', () => {
          setLoadingState((prev) => ({
            isLoading: true,
            error: null,
            retryCount: prev.retryCount + 1,
          }))
        })
      }
    } else if (loadingState.isLoading) {
      window.showRootLoader?.('Loading application...')
    } else {
      // Keep loader visible with "Checking Session..." so SessionAuth can use same UI until session check completes
      window.showRootLoader?.('Checking Session...')
    }

    if (!loadingState.isLoading || loadingState.error) return undefined

    const connection = getNavigatorConnection()
    if (!connection) return undefined

    const emitIfSlowBoot = () => {
      if (isSlowBootConnectionHint(connection)) {
        window.dispatchEvent(new CustomEvent(BOOT_APP_3G_SLOW_EVENT))
      }
    }

    emitIfSlowBoot()
    connection.addEventListener('change', emitIfSlowBoot)
    return () => {
      connection.removeEventListener('change', emitIfSlowBoot)
    }
  }, [loadingState])

  // Main initialization with timeout and error handling
  useEffect(() => {
    if (!loadingState.isLoading) return

    let isMounted = true
    let timeoutId = null

    // Set timeout to prevent indefinite loading
    timeoutId = setTimeout(() => {
      if (isMounted) {
        setLoadingState({
          isLoading: false,
          error: 'Loading timed out. Please check your connection and try again.',
          retryCount: loadingState.retryCount,
        })
      }
    }, LOADING_TIMEOUT)

    const initialize = async () => {
      try {
        setIsIOS(/iPhone|iPad|iPod/i.test(navigator.userAgent))

        // Handle iOS-specific reload with better error handling and prevention of infinite loops
        if (isIOS) {
          try {
            // Check if we can access sessionStorage (fails in private browsing mode)
            const canUseSessionStorage = (() => {
              try {
                const testKey = '__storage_test__'
                sessionStorage.setItem(testKey, 'test')
                sessionStorage.removeItem(testKey)
                return true
              } catch (e) {
                return false
              }
            })()

            if (canUseSessionStorage) {
              const iosReloaded = sessionStorage.getItem('iosReloaded')
              const reloadAttempts = parseInt(sessionStorage.getItem('iosReloadAttempts') || '0')

              // Prevent infinite reload loops by limiting attempts
              if (!iosReloaded && reloadAttempts < 2) {
                sessionStorage.setItem('iosReloaded', 'true')
                sessionStorage.setItem('iosReloadAttempts', (reloadAttempts + 1).toString())
                window.location.reload()
                return
              }

              // Clear reload attempts after successful load
              if (iosReloaded) {
                sessionStorage.removeItem('iosReloadAttempts')
              }
            } else {
              // If sessionStorage doesn't work (private browsing), use localStorage as fallback
              try {
                const iosReloaded = localStorage.getItem('iosReloaded')
                const reloadAttempts = parseInt(localStorage.getItem('iosReloadAttempts') || '0')

                if (!iosReloaded && reloadAttempts < 2) {
                  localStorage.setItem('iosReloaded', 'true')
                  localStorage.setItem('iosReloadAttempts', (reloadAttempts + 1).toString())
                  // Add a small delay before reload to prevent rapid reload loops
                  setTimeout(() => {
                    window.location.reload()
                  }, 100)
                  return
                }

                if (iosReloaded) {
                  localStorage.removeItem('iosReloadAttempts')
                }
              } catch (localStorageError) {
                console.warn(
                  'Both sessionStorage and localStorage failed on iOS, continuing without reload:',
                  localStorageError,
                )
                // Continue without reload if both storage methods fail
              }
            }
          } catch (storageError) {
            console.warn('iOS reload handling failed, continuing with app initialization:', storageError)
            // Continue with app initialization even if reload logic fails
          }
        }

        // Fetch user data
        const _token = await storage.getToken()
        const user = await storage.getUser()

        if (user && isMounted) {
          const userObj = JSON.parse(user)
          setFormattedUser(userObj)
        }

        // Mark loading as complete if we reach here without errors
        if (isMounted) {
          setLoadingState({
            isLoading: false,
            error: null,
            retryCount: loadingState.retryCount,
          })
        }
      } catch (error) {
        console.error('Initialization error:', error)
        if (isMounted) {
          setLoadingState({
            isLoading: false,
            error: error.message || 'Failed to initialize application',
            retryCount: loadingState.retryCount,
          })
        }
      }
    }

    initialize()

    return () => {
      isMounted = false
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [isIOS, loadingState.isLoading, loadingState.retryCount])

  // Render fallback for critical errors
  if (loadingState.error && loadingState.retryCount > 3) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          padding: '20px',
          textAlign: 'center',
        }}
      >
        <h2>Unable to load application</h2>
        <p>{loadingState.error}</p>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '10px 20px',
            background: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginTop: '20px',
          }}
        >
          Reload Application
        </button>
      </div>
    )
  }

  return (
    <HelmetProvider>
      <Helmet>
        <title>My App</title>
      </Helmet>
      <SettingsProvider>
        <CollapseDrawerProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </CollapseDrawerProvider>
      </SettingsProvider>
    </HelmetProvider>
  )
}

// Global functions to control root loader (open/close use same 0.3s opacity transition)
const ROOT_LOADER_TRANSITION_MS = 300

window.showRootLoader = (message = 'Loading application...') => {
  const rootLoader = document.getElementById('root-loader')
  const loaderStatus = document.getElementById('root-loader-status')
  if (rootLoader) {
    rootLoader.style.display = 'flex'
    rootLoader.style.opacity = '0'
    // Force reflow so opacity 0 is applied before we transition to 1
    rootLoader.offsetHeight
    requestAnimationFrame(() => {
      rootLoader.style.opacity = '1'
    })
  }
  if (loaderStatus) {
    loaderStatus.innerHTML = message
    loaderStatus.classList.add('blink')
  }
}

window.hideRootLoader = () => {
  const rootLoader = document.getElementById('root-loader')
  if (!rootLoader) return
  rootLoader.style.transition = `opacity ${ROOT_LOADER_TRANSITION_MS}ms ease-out`
  rootLoader.style.opacity = '0'
  setTimeout(() => {
    rootLoader.style.display = 'none'
  }, ROOT_LOADER_TRANSITION_MS)
}

// Initialize the root loader visibility
document.addEventListener('DOMContentLoaded', () => {
  const rootLoader = document.getElementById('root-loader')
  if (rootLoader) {
    rootLoader.style.display = 'flex'
  }
})

const container = document.getElementById('root')
let root = window.__sparkleAppRoot
if (!root) {
  root = createRoot(container)
  window.__sparkleAppRoot = root
}
root.render(
  <React.StrictMode>
    <AppWithLoader />
  </React.StrictMode>,
)

// If you want to enable client cache, register instead.
serviceWorkerRegistration.unregister()

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
reportWebVitals()
